import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/views/auth/shared/auth.service';

/**
 * Persistência de "ocultar ajuda nesta tela", por rota + por usuário
 * (`specs/abcx/ai-help-fab-visibility.md`, item 2 — ainda não implementado no
 * abcx no momento deste porte para o Wazir; F0 aqui é localStorage por
 * usuário, igual ao previsto na spec: "preferência do usuário no backend, ou
 * localStorage por usuário como F0").
 *
 * Granularidade: por rota (normalizada, sem query/fragment) + por usuário.
 * Default: HABILITADO (nenhuma rota oculta até o usuário ocultar).
 */
@Injectable()
export class AiHelpVisibilityService {
  private static readonly STORAGE_PREFIX = '@wazir:ai-help-hidden-screens:';

  constructor(private readonly authService: AuthService) {}

  /** Normaliza a rota removendo query string e fragment. */
  normalizeRoute(url: string): string {
    if (!url) return '/';
    return url.split('?')[0].split('#')[0] || '/';
  }

  isHidden(url: string): boolean {
    const route = this.normalizeRoute(url);
    return this.readHiddenRoutes().includes(route);
  }

  hide(url: string): void {
    const route = this.normalizeRoute(url);
    const routes = this.readHiddenRoutes();
    if (!routes.includes(route)) {
      routes.push(route);
      this.writeHiddenRoutes(routes);
    }
  }

  show(url: string): void {
    const route = this.normalizeRoute(url);
    const routes = this.readHiddenRoutes().filter((r) => r !== route);
    this.writeHiddenRoutes(routes);
  }

  toggle(url: string): void {
    if (this.isHidden(url)) {
      this.show(url);
    } else {
      this.hide(url);
    }
  }

  /** Chave por usuário — cada usuário tem sua própria preferência. */
  private storageKey(): string {
    let userId: string | number = 'anon';
    try {
      const user = this.authService.getUser?.();
      if (user?.id) userId = user.id;
    } catch {
      // sem usuário logado (ex.: tela de login) — usa o bucket 'anon'
    }
    return `${AiHelpVisibilityService.STORAGE_PREFIX}${userId}`;
  }

  private readHiddenRoutes(): string[] {
    try {
      const raw = window.localStorage.getItem(this.storageKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writeHiddenRoutes(routes: string[]): void {
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify(routes));
    } catch {
      // localStorage indisponível (modo privado etc.) — falha silenciosa,
      // apenas não persiste entre reloads.
    }
  }
}
